import React, { useEffect, useRef, useState, createRef, Children, useMemo, useCallback } from "react";
import Select, { components } from "react-select";
import styled from "styled-components";
import Search from '@material-ui/icons/Search';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { useTranslation } from "react-i18next";
import './DropdownCustom.css'
import DOMPurify from 'dompurify';
import debounce from 'lodash.debounce';

const CustomInput = styled.input`
  display: flex;
  align-items: center;
  width: 100%;
  &:focus-visible {
    outline: none !important;
  }
`;

const { MenuList, ValueContainer, SingleValue, Placeholder, Option } = components;

const CustomOption = ({ children, isSelected, innerProps, ...props }) => {
  const { enableCheckbox } = props.selectProps;
  
  const handleClick = (event) => {
    // Call parent menu's onClick handler
    event.stopPropagation();
    event.preventDefault();
    if (innerProps.onClick) {
      innerProps.onClick(event);
    }
  };

  return (
    <components.Option 
      {...props} 
      innerProps={{innerProps, onClick: handleClick}}
      onClick={handleClick}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {enableCheckbox && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(event) => {
              event.stopPropagation();
              event.preventDefault();
            }}
            style={{ marginRight: '8px' }}
          />
        )}
        {children}
      </div>
    </components.Option>
  );
};
 

const CustomMenuList = ({ selectProps, ...props }) => {
  const { onInputChange, inputValue, onMenuInputFocus, searchInpValue, enableSearch } = selectProps;
  const { t } = useTranslation();

  const sanitizeInput = (input) => {
    // Sanitize input to prevent SQL injection, XSS, and other attacks
    // Allow phonetics used in Central European languages
    const sanitized = DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [], // No HTML tags allowed
      ALLOWED_ATTR: [], // No attributes allowed
    });

    // Regex that allows letters (including accented characters), numbers, and common punctuation
    return sanitized.replace(/[^a-zA-Z0-9\s\u00C0-\u024F\u1E00-\u1EFF\_',.-]/g, '');
  };

  const handleInputChange = (e) => {
    const sanitizedValue = sanitizeInput(e.currentTarget.value);
    // Limit input to 256 characters
    const truncatedValue = sanitizedValue.slice(0, 256);
    onInputChange(truncatedValue, {
      action: "input-change"
    });
  };

  // Copied from source
  const ariaAttributes = {
    "aria-autocomplete": "list",
    "aria-label": selectProps["aria-label"],
    "aria-labelledby": selectProps["aria-labelledby"]
  };

  return (
    <div>
      {enableSearch && <span style={{ display: 'flex', alignItems: 'center', }}>
        <Search style={{ position: 'absolute', fontSize: '15px', marginLeft: '5px' }} />
        <CustomInput
          className="searchInp-inside-select"
          autoCorrect="off"
          autoComplete="off"
          spellCheck="false"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.target.focus();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.target.focus();
          }}
          onFocus={onMenuInputFocus}
          placeholder={t('dropdown.search')}
          maxLength={256}
          {...ariaAttributes}
        />
      </span>
      }
      <MenuList {...props} selectProps={selectProps} />
    </div>
  );
};

// Set custom `SingleValue` and `Placeholder` to keep them when searching
const CustomValueContainer = ({ children, selectProps, ...props }) => {
  const commonProps = {
    cx: props.cx,
    clearValue: props.clearValue,
    getStyles: props.getStyles,
    getValue: props.getValue,
    hasValue: props.hasValue,
    isMulti: props.isMulti,
    isRtl: props.isRtl,
    options: props.options,
    selectOption: props.selectOption,
    setValue: props.setValue,
    selectProps,
    theme: props.theme
  };

  return (
    <components.ValueContainer {...props} selectProps={selectProps}>
      {React.Children.map(children, (child) => {
        return child ? (
          child
        ) : props.hasValue ? (
          <components.SingleValue
            {...commonProps}
            isFocused={selectProps.isFocused}
            isDisabled={selectProps.isDisabled}
          >
            {selectProps.getOptionLabel(props.getValue()[0])}
          </components.SingleValue>
        ) : (
          <components.Placeholder
            {...commonProps}
            key="placeholder"
            isDisabled={selectProps.isDisabled}
            data={props.getValue()}
          >
            {selectProps.placeholder}
          </components.Placeholder>
        );
      })}
    </components.ValueContainer>
  );
};

const SelectedOptionsDisplay = styled.div`
  margin-top: 10px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SingleSelect = (props) => {
  const {
    onChange, //onchange function on changing select value
    selectOpts = [], //options that needs to be selected
    defaultValue, // default values that needs to be shown
    resetSelect, // to rese the select
    selectVal, // current value that needs to be displayed on select
    isDisabled, // to disabld the select //BOOLEAN
    isSearchable, // to enable disable //BOOLEAN
    onSearchInpChange, // to get the value typed inside search
    customFilterOption, // custom filter
    isLoading, // to displayloading icon,
    isClearable,
    selectOnBlur,
    name,
    containerStyle = {},
    enableCheckbox = false,
    noOptionsMessage = "No results found",
    customPlaceholder = "Select...",
    closeMenuOnSelect = true,
    searchOnTextChange = false,
    customStyles = {
      menuList: {},
      option: {}
    },
    openDropDownInBody = false
  } = props;

  // console.log("selectVal ====",selectVal)
  // console.log("selectVal ==== selectOpts",selectOpts)
  const containerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [userInputVal, setUserInputVal] = useState("")
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [cspNonce, setCspNonce] = useState('');
  const valueRef = useRef(props.selectVal);
  valueRef.current = props.selectVal;
  let myRef = createRef();

  const options = useMemo(() => selectOpts, [selectOpts]);

  // Debounced search function (using lodash)
  const debouncedSearch = useCallback(
    debounce((inputValue) => {
      onSearchInpChange(inputValue, 'ENTERHIT');
    }, 500), // Debounce delay (500ms)
    [] // Empty dependency array ensures the debounce function is created once
  );


  useEffect(() => {
    const metaTag = document.querySelector('meta[property="csp-nonce"]');
    if (metaTag) {
      setCspNonce(metaTag.content);
    }

    const onDomClick = (e) => {
      if (containerRef.current) {
        let menu = containerRef.current.querySelector(".select__menu");
  
        if (
          !containerRef.current.contains(e.target) ||
          !menu ||
          !menu.contains(e.target)
        ) {
          setIsFocused(false);
          setInputValue("");
        }
      }
    };

    document.addEventListener("mousedown", onDomClick);

    return () => {
      document.removeEventListener("mousedown", onDomClick);
    };
  }, []);


  useEffect(() => {
    if (resetSelect) {
      setSelectedOptions([]);
    }
  }, [resetSelect]);

  
  function onKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      let menuIsOpen = false;
      menuIsOpen = myRef.current.props.menuIsOpen;
      if (menuIsOpen) {
        onSearchInpChange(inputValue, 'ENTERHIT');
      }
    } 
  }

  const isSelectAllSelected = () =>
    valueRef?.current?.length === props.selectOpts?.length && props.selectOpts.find(i => i.value === 'select-all');

  const isOptionSelected = option => {
    if (enableCheckbox) {
      return valueRef?.current?.some(({ value }) => value === option.value) ||
      isSelectAllSelected();
    }
  }

  const optionChange = (selectedOption, actionMeta) => {
    if (enableCheckbox) {
      const { action, option, removedValue } = actionMeta;
      if (action === "select-option" && option.value === 'select-all') {
        props.onChange(props.selectOpts, actionMeta);
      } else if (
        (action === "deselect-option" && option.value === 'select-all') ||
        (action === "remove-value" && removedValue.value === 'select-all')
      ) {
        props.onChange([], actionMeta);
      } else if (
        actionMeta.action === "deselect-option" &&
        isSelectAllSelected()
      ) {
        props.onChange(
          props.selectOpts.filter(({ value }) => value !== option.value && value !== 'select-all'),
          actionMeta
        );
      } else {
        props.onChange(selectedOption || [], actionMeta);
      }
    } else {
      onChange({ target: { label: selectedOption?.value, value: selectedOption?.value } })
      setIsFocused(false)
    }
  };

  function onChangeInput(value, options) {
    const sanitizedValue = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
    // Allow phonetics and limit to 256 characters
    const processedValue = sanitizedValue
    .replace(/[^a-zA-Z0-9\s\u00C0-\u024F\u1E00-\u1EFF\_',.-]/g, '')
      .slice(0, 256);

    setInputValue(processedValue);
    if(searchOnTextChange && !["input-blur", "menu-close"].includes(options?.action)) {
      debouncedSearch(processedValue);
    } else if (!processedValue) {
      onSearchInpChange(processedValue, 'EMPTY');
    }
  }

  const cache = createCache({
    key: 'css',
    //nonce: document.querySelector('meta[property="csp-nonce"]')?.content || '',
    nonce: cspNonce, //document.querySelector('meta[property="csp-nonce"]')?.content || '',
  });

  const getValue = () => {
    if (enableCheckbox) {
      return isSelectAllSelected() ? [props.selectOpts.find(i => i.value == "select-all")] : valueRef?.current;
    } else if(selectVal && Array.isArray(selectVal)) {
      return options.find(option => option.value === selectVal[0]?.value);
    } else {
      return selectVal ? options.find(option => option.value === selectVal.value) : null;
    }
  }

  const NoOptionsMessage = props => {
    return (
      <components.NoOptionsMessage {...props}>
        <span>{noOptionsMessage}</span>
      </components.NoOptionsMessage>
    );
  };

  const MoreSelectedBadge = ({ items }) => {
    const style = {
      marginLeft: "auto",
      background: "#d4eefa",
      borderRadius: "4px",
      fontFamily: "Open Sans",
      fontSize: "11px",
      padding: "3px",
      order: 99
    };
  
    const title = items.join(", ");
    const length = items.length;
    const label = `+ ${length}`;
  
    return (
      <div style={style} title={title}>
        {label}
      </div>
    );
  };
  
  const MultiValue = ({ index, getValue, ...props }) => {
    const maxToShow = 1;
    const overflow = getValue()
      .slice(maxToShow)
      .map((x) => x.label);
  
    return index < maxToShow ? (
      <components.MultiValue {...props} styles={{ maxWidth: '70%' }}/>
    ) : index === maxToShow ? (
      <MoreSelectedBadge items={overflow} />
    ) : null;
  };

  return (
    <div id="dropDown" ref={containerRef}>
      <CacheProvider value={cache}>
        <Select
          id={props.id}
          styles={{
            menuList: (base) => ({
              ...base,
              overflowX: "hidden",
              "::-webkit-scrollbar": {
                width: "4px",
                height: "30px",
              },
              "::-webkit-scrollbar-track": {
                background: "#F1F1F1"
              },
              "::-webkit-scrollbar-thumb": {
                background: "#888"
              },
              "::-webkit-scrollbar-thumb:hover": {
                background: "#555"
              },
              ...(customStyles?.menuList || {})
            }),
            dropdownIndicator: (provided, state) => ({
              ...provided,
              transform: state.selectProps.menuIsOpen && "rotate(180deg)",
            }),
            option: (provided, state) => ({
              ...provided,
              padding: props.changeHeight? '3px 12px':'8px 12px',
              margin: '0',
              ...(customStyles?.option || {})
            }),
            container: (provided, state) => ({
              ...provided,
              ...containerStyle,
            }),
            noOptionsMessage: (base) => ({ ...base }),
            placeholder: (base) => ({
              ...base,
            }),
            singleValue: (base) => ({ 
              ...base,
            }),
            valueContainer: (base) => ({
              ...base,
            }),
            multiValue: (base) => ({
              ...base,
              maxWidth: '70%',
              backgroundColor: 'transparent',
            }),
            multiValueRemove: (base) => ({ ...base, display: 'none' }),
            menuPortal: (base) => ({ ...base, zIndex: openDropDownInBody ? 99999 : undefined }),
          }}
          onKeyDown={onKeyDown}
          ref={myRef}
          isDisabled={isDisabled}
          isLoading={isLoading}
          className="basic-single"
          classNamePrefix="select"
          name={name}
          inputId={name}
          enableSearch={isSearchable}
          options={selectOpts}
          isOptionDisabled={(option) => option.isdisabled}
          isMulti={enableCheckbox}
          closeMenuOnSelect={closeMenuOnSelect}
          hideSelectedOptions={false}
          components={{
            MenuList: CustomMenuList,
            // ValueContainer: CustomValueContainer,
            // Input,
            ...(enableCheckbox ? {Option: CustomOption} : {}),
            //Option: CustomOption,
            IndicatorSeparator: null,
            noOptionsMessage: NoOptionsMessage,
            // MultiValue
            ...(enableCheckbox ? {MultiValue}  : {}),
          }}
          noOptionsMessage = {() => noOptionsMessage}
          maxMenuHeight={200}
          //value={enableCheckbox ? selectedOptions : selectVal}
          value={getValue()}
          menuIsOpen={isFocused}
          isClearable={isClearable}
          // isClearable={selectVal?.value === 'All Sites' || selectVal?.value === 'Filter' ? false : true}
          inputValue={inputValue}
          searchInpValue={userInputVal}
          isSearchable={false}
          onMenuInputFocus={() => setIsFocused(true)}
          // onChange={() => setIsFocused(false)}
          onChange={optionChange}
          enableCheckbox={enableCheckbox}
          onInputChange={isSearchable ? onChangeInput : false}
          // onInputChange={(val) => setInputValue(val)}
          defaultValue={defaultValue}
          // defaultValue={getValue()}
          {...{
            menuIsOpen: isFocused || undefined,
            isFocused: isFocused || undefined
          }}
          filterOption={customFilterOption}
          blurInputOnSelect={closeMenuOnSelect}
          placeholder={customPlaceholder}
          isOptionSelected={isOptionSelected}
          menuPortalTarget={openDropDownInBody ? document.body : undefined}
        />
      </CacheProvider>
    </div>
  );
};

export default SingleSelect;
